<template>
  <v-container>
    <div class="d-flex justify-space-between align-center mt-10 mb-6">
      <div class="font-weight-bold title-page">Associar procurador</div>
      <v-btn outlined color="primary" to="/minha-conta">Voltar</v-btn>
    </div>

    <v-stepper v-model="step" vertical>
      <v-stepper-step :complete="step > 1" step="1">Cadastro do credor</v-stepper-step>
      <v-stepper-content step="1">
        <v-alert v-if="workflow.readiness && workflow.readiness.ready" type="success" outlined>
          Seus dados cadastrais estão completos.
        </v-alert>
        <template v-else-if="workflow.readiness">
          <v-alert type="warning" outlined>
            Complete os dados cadastrais necessários antes de iniciar a associação.
            <ul v-if="workflow.readiness.missingFields.length" class="mt-2">
              <li v-for="item in workflow.readiness.missingFields" :key="item.field">{{ item.label }}</li>
            </ul>
          </v-alert>
          <v-alert v-for="blocker in workflow.readiness.blockers" :key="blocker.code" type="info" outlined>
            {{ blocker.message }}
          </v-alert>
        </template>
        <v-btn v-if="workflow.readiness && !workflow.readiness.ready" color="primary"
          @click="profileModal = true">Completar cadastro</v-btn>
        <v-btn v-else color="primary" @click="continueFromProfile">
          {{ association && [0, 1].includes(Number(association.status)) ? 'Ver acompanhamento' : 'Continuar' }}
        </v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 2" step="2">Dados do procurador</v-stepper-step>
      <v-stepper-content step="2">
        <v-form ref="procuratorForm">
          <v-row dense>
            <v-col cols="12" sm="6"><v-text-field v-model="procurator.name" label="Nome completo" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="procurator.registration" label="CPF" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="procurator.email" label="E-mail" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="procurator.phone_number" label="Telefone" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="6"><v-text-field v-model="procurator.federal_registration" label="OAB" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="6"><v-autocomplete v-model="procurator.federal_registration_uf" :items="states" item-text="name" item-value="sigla" label="UF da OAB" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="procurator.address_postal_code" label="CEP" outlined dense hide-details="auto" :rules="required" @blur="fetchCep" /></v-col>
            <v-col cols="12" sm="8"><v-text-field v-model="procurator.address_street" label="Rua/Avenida" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="procurator.address_number" label="Número" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="8"><v-text-field v-model="procurator.address_complement" label="Complemento" outlined dense hide-details="auto" /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="procurator.address_neighborhood" label="Bairro" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="4"><v-text-field v-model="procurator.address_city" label="Cidade" outlined dense hide-details="auto" :rules="required" /></v-col>
            <v-col cols="12" sm="4"><v-autocomplete v-model="procurator.address_state" :items="states" item-text="name" item-value="sigla" label="Estado" outlined dense hide-details="auto" :rules="required" /></v-col>
          </v-row>
          <input ref="procuratorDocumentInput" type="file" class="d-none"
            accept="application/pdf,image/png,image/jpeg" @change="selectProcuratorDocument" />
          <div class="document-drop-zone mb-3" :class="{
            'document-drop-zone--active': draggingDocument,
            'document-drop-zone--disabled': procurator.hasDocument
          }" @click="openProcuratorDocumentPicker" @dragenter.prevent="draggingDocument = true"
            @dragover.prevent="draggingDocument = true" @dragleave.prevent="draggingDocument = false"
            @drop.prevent="dropProcuratorDocument">
            <v-icon color="primary" size="36">mdi-cloud-upload-outline</v-icon>
            <div class="font-weight-bold mt-2">
              {{ procurator.hasDocument ? 'Documento do procurador já cadastrado' : 'Arraste o documento do procurador' }}
            </div>
            <div v-if="!procurator.hasDocument" class="caption grey--text text--darken-1 mt-1">
              ou clique para selecionar — PDF, PNG ou JPG de até 4 MB
            </div>
          </div>

          <v-card v-if="procuratorFile && procuratorDocumentPreviewUrl" outlined class="document-preview mb-4">
            <v-card-title class="subtitle-2 py-2">
              <v-icon left small>mdi-file-eye-outline</v-icon>
              {{ procuratorFile.name }}
              <v-spacer />
              <span class="caption grey--text mr-2">{{ formatFileSize(procuratorFile.size) }}</span>
              <v-btn icon small aria-label="Remover documento" @click="clearProcuratorDocument">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-img v-if="procuratorDocumentIsImage" :src="procuratorDocumentPreviewUrl"
              contain max-height="360" class="grey lighten-4" />
            <iframe v-else :src="procuratorDocumentPreviewUrl" title="Pré-visualização do documento do procurador"
              class="document-preview__pdf" />
          </v-card>

          <v-alert v-if="procurator.hasDocument && !procuratorFile" type="success" dense outlined>
            O documento já cadastrado será utilizado nesta associação.
          </v-alert>
        </v-form>
        <v-btn color="primary" :loading="saving" @click="saveProcurator">Salvar e continuar</v-btn>
        <v-btn text @click="step = 1">Voltar</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 3" step="3">Revisar e gerar procuração</v-stepper-step>
      <v-stepper-content step="3">
        <v-card outlined class="pa-4 mb-4">
          <div><strong>Procurador:</strong> {{ procurator.name }}</div>
          <div><strong>CPF:</strong> {{ procurator.registration }}</div>
          <div><strong>OAB:</strong> {{ procurator.federal_registration }} / {{ procurator.federal_registration_uf }}</div>
          <div><strong>E-mail:</strong> {{ procurator.email }}</div>
          <div><strong>Endereço:</strong> {{ procurator.address_street }}, {{ procurator.address_number }} — {{ procurator.address_city }}/{{ procurator.address_state }}</div>
        </v-card>
        <v-btn color="primary" :loading="generating" @click="generateProcuration">Gerar e baixar PDF</v-btn>
        <v-btn text @click="step = 2">Corrigir dados</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 4" step="4">Assinar e enviar</v-stepper-step>
      <v-stepper-content step="4">
        <v-alert type="info" outlined>
          Assine o PDF original pelo GOV.BR sem convertê-lo, imprimi-lo ou digitalizá-lo.
        </v-alert>
        <v-btn v-if="association && association.draftDocumentUrl" :href="association.draftDocumentUrl"
          target="_blank" outlined color="primary" class="mb-4">Baixar procuração novamente</v-btn>
        <input ref="signedDocumentInput" type="file" class="d-none" accept="application/pdf"
          @change="selectSignedDocument" />
        <div class="document-drop-zone mb-3" :class="{ 'document-drop-zone--active': draggingSignedDocument }"
          @click="openSignedDocumentPicker" @dragenter.prevent="draggingSignedDocument = true"
          @dragover.prevent="draggingSignedDocument = true" @dragleave.prevent="draggingSignedDocument = false"
          @drop.prevent="dropSignedDocument">
          <v-icon color="primary" size="36">mdi-file-sign</v-icon>
          <div class="font-weight-bold mt-2">
            {{ signedFile ? 'Procuração assinada selecionada' : 'Arraste a procuração assinada' }}
          </div>
          <div class="caption grey--text text--darken-1 mt-1">
            {{ signedFile ? 'Clique ou arraste outro PDF para substituir' : 'ou clique para selecionar — PDF de até 10 MB' }}
          </div>
        </div>

        <v-card v-if="signedFile && signedDocumentPreviewUrl" outlined class="document-preview mb-4">
          <v-card-title class="subtitle-2 py-2">
            <v-icon left small>mdi-file-eye-outline</v-icon>
            {{ signedFile.name }}
            <v-spacer />
            <span class="caption grey--text mr-2">{{ formatFileSize(signedFile.size) }}</span>
            <v-btn icon small aria-label="Remover procuração assinada" @click="clearSignedDocument">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <iframe :src="signedDocumentPreviewUrl" title="Pré-visualização da procuração assinada"
            class="document-preview__pdf" />
        </v-card>
        <v-btn color="primary" :loading="uploading" :disabled="!signedFile"
          @click="uploadSigned">Enviar procuração</v-btn>
        <v-btn text @click="step = 3">Voltar</v-btn>
      </v-stepper-content>

      <v-stepper-step step="5">Acompanhamento</v-stepper-step>
      <v-stepper-content step="5">
        <v-alert :type="statusType" outlined>
          <strong>{{ statusLabel }}</strong><br>
          <span v-if="association && (association.validationError || association.reason)">
            {{ association.validationError || association.reason }}
          </span>
        </v-alert>
        <v-btn v-if="association && [2, 5].includes(Number(association.status))" color="primary" @click="step = 2">
          Atualizar dados
        </v-btn>
        <v-btn outlined color="primary" to="/minha-conta">Concluir</v-btn>
      </v-stepper-content>
    </v-stepper>

    <user-profile-edit-modal v-model="profileModal" @saved="reloadAfterProfile" />
  </v-container>
</template>

<script>
import axios from 'axios'
import states from '@/common/data/states-list.data'
import UserProfileEditModal from '@/components/creditor/UserProfileEditModal.vue'

const emptyProcurator = () => ({
  name: '', registration: '', email: '', phone_number: '', federal_registration: '',
  federal_registration_uf: '', address_postal_code: '', address_street: '', address_number: '',
  address_complement: '', address_neighborhood: '', address_city: '', address_state: '', hasDocument: false
})

export default {
  name: 'LawyerAssociationWorkflow',
  components: { UserProfileEditModal },
  data: () => ({
    step: 1,
    workflow: { readiness: null, association: null },
    association: null,
    procurator: emptyProcurator(),
    procuratorFile: null,
    procuratorDocumentPreviewUrl: null,
    draggingDocument: false,
    signedFile: null,
    signedDocumentPreviewUrl: null,
    draggingSignedDocument: false,
    profileModal: false,
    states,
    saving: false,
    generating: false,
    uploading: false
  }),
  computed: {
    required() { return [this.$rules.required] },
    procuratorDocumentIsImage() {
      return Boolean(this.procuratorFile && (
        this.procuratorFile.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(this.procuratorFile.name)
      ))
    },
    statusLabel() {
      const status = Number(this.association && this.association.status)
      return ({ 0: 'Em análise', 1: 'Associação aprovada', 2: 'Associação rejeitada', 3: 'Associação removida', 4: 'Aguardando procuração assinada', 5: 'Atualização de dados necessária' })[status] || 'Processamento iniciado'
    },
    statusType() {
      const status = Number(this.association && this.association.status)
      if (status === 1) return 'success'
      if ([2, 5].includes(status)) return 'error'
      return 'info'
    }
  },
  created() { this.loadWorkflow() },
  beforeDestroy() {
    this.revokeProcuratorDocumentPreview()
    this.revokeSignedDocumentPreview()
  },
  methods: {
    continueFromProfile() {
      this.step = this.association && [0, 1].includes(Number(this.association.status)) ? 5 : 2
    },
    async loadWorkflow() {
      const response = await axios.get('/api/users/me/lawyer-association')
      this.workflow = response.data.content
      this.association = this.workflow.association
      if (this.association && this.association.procurator) {
        this.procurator = Object.assign(emptyProcurator(), this.association.procurator)
      }

      const associationStatus = this.association ? Number(this.association.status) : null
      const draftStatus = this.association ? Number(this.association.draftStatus) : null
      const hasGeneratedProcuration = Boolean(
        this.association &&
        this.association.draftDocumentUrl &&
        (draftStatus === 1 || associationStatus === 4)
      )

      if (hasGeneratedProcuration) {
        this.step = 4
      } else if ([0, 1].includes(associationStatus)) {
        this.step = 5
      }
    },
    async reloadAfterProfile() { await this.loadWorkflow() },
    async fetchCep() {
      const cep = String(this.procurator.address_postal_code || '').replace(/\D/g, '')
      if (cep.length !== 8) return
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)
      if (response.data && !response.data.erro) {
        this.procurator.address_street = response.data.logradouro
        this.procurator.address_neighborhood = response.data.bairro
        this.procurator.address_city = response.data.localidade
        this.procurator.address_state = response.data.uf
      }
    },
    openProcuratorDocumentPicker() {
      if (!this.procurator.hasDocument) this.$refs.procuratorDocumentInput.click()
    },
    selectProcuratorDocument(event) {
      const file = event.target.files && event.target.files[0]
      this.setProcuratorDocument(file)
      event.target.value = ''
    },
    dropProcuratorDocument(event) {
      this.draggingDocument = false
      if (this.procurator.hasDocument) return
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      this.setProcuratorDocument(file)
    },
    setProcuratorDocument(file) {
      if (!file) return
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
      const hasAllowedExtension = /\.(pdf|png|jpe?g)$/i.test(file.name)
      if (!allowedTypes.includes(file.type) && !hasAllowedExtension) {
        this.$dialog.notify.error('Selecione um documento em PDF, PNG ou JPG.')
        return
      }
      if (file.size > 4 * 1024 * 1024) {
        this.$dialog.notify.error('O documento deve possuir no máximo 4 MB.')
        return
      }
      this.revokeProcuratorDocumentPreview()
      this.procuratorFile = file
      this.procuratorDocumentPreviewUrl = URL.createObjectURL(file)
    },
    clearProcuratorDocument(event) {
      if (event) event.stopPropagation()
      this.revokeProcuratorDocumentPreview()
      this.procuratorFile = null
    },
    revokeProcuratorDocumentPreview() {
      if (this.procuratorDocumentPreviewUrl) URL.revokeObjectURL(this.procuratorDocumentPreviewUrl)
      this.procuratorDocumentPreviewUrl = null
    },
    formatFileSize(size) {
      if (!size) return '0 KB'
      return `${(size / 1024 / 1024).toFixed(2)} MB`
    },
    openSignedDocumentPicker() {
      this.$refs.signedDocumentInput.click()
    },
    selectSignedDocument(event) {
      const file = event.target.files && event.target.files[0]
      this.setSignedDocument(file)
      event.target.value = ''
    },
    dropSignedDocument(event) {
      this.draggingSignedDocument = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      this.setSignedDocument(file)
    },
    setSignedDocument(file) {
      if (!file) return
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
      if (!isPdf) {
        this.$dialog.notify.error('Selecione a procuração assinada em formato PDF.')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$dialog.notify.error('A procuração assinada deve possuir no máximo 10 MB.')
        return
      }
      this.revokeSignedDocumentPreview()
      this.signedFile = file
      this.signedDocumentPreviewUrl = URL.createObjectURL(file)
    },
    clearSignedDocument(event) {
      if (event) event.stopPropagation()
      this.revokeSignedDocumentPreview()
      this.signedFile = null
    },
    revokeSignedDocumentPreview() {
      if (this.signedDocumentPreviewUrl) URL.revokeObjectURL(this.signedDocumentPreviewUrl)
      this.signedDocumentPreviewUrl = null
    },
    async saveProcurator() {
      if (!this.$refs.procuratorForm.validate()) return
      if (!this.procurator.hasDocument && !this.procuratorFile) {
        this.$dialog.notify.error('Envie o documento do procurador para continuar.')
        return
      }
      this.saving = true
      try {
        const payload = new FormData()
        Object.keys(this.procurator).filter(key => key !== 'hasDocument').forEach(key => {
          let value = this.procurator[key]
          if (['registration', 'address_postal_code'].includes(key)) value = String(value || '').replace(/\D/g, '')
          payload.append(key, value || '')
        })
        if (this.procuratorFile) payload.append('procurator_file', this.procuratorFile)
        const response = await axios.patch('/api/users/me/lawyer-association/procurator', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.clearProcuratorDocument()
        this.workflow = response.data.content
        this.association = this.workflow.association
        this.procurator = Object.assign(emptyProcurator(), this.association.procurator)
        this.step = 3
      } catch (error) {
        this.notifyError(error, 'Não foi possível salvar os dados do procurador.')
      } finally { this.saving = false }
    },
    async generateProcuration() {
      this.generating = true
      try {
        const response = await axios.post('/api/users/me/lawyer-association/procuration/generate', {}, { responseType: 'blob' })
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        link.download = 'procuracao.pdf'
        link.click()
        URL.revokeObjectURL(url)
        await this.loadWorkflow()
        this.step = 4
      } catch (error) {
        this.$dialog.notify.error('Não foi possível gerar a procuração.')
      } finally { this.generating = false }
    },
    async uploadSigned() {
      if (!this.signedFile) return
      this.uploading = true
      try {
        const payload = new FormData()
        payload.append('signed_pdf', this.signedFile)
        const response = await axios.post('/api/users/me/lawyer-association/procuration/upload', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.workflow = response.data.content
        this.association = this.workflow.association
        this.clearSignedDocument()
        this.step = 5
      } catch (error) {
        this.notifyError(error, 'Não foi possível enviar a procuração.')
      } finally { this.uploading = false }
    },
    notifyError(error, fallback) {
      const message = error.response && error.response.data && error.response.data.message
      this.$dialog.notify.error(message || fallback)
    }
  }
}
</script>

<style scoped>
.document-drop-zone {
  align-items: center;
  border: 2px dashed #9e9e9e;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 132px;
  padding: 20px;
  text-align: center;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.document-drop-zone:hover,
.document-drop-zone--active {
  background: #f3f5ff;
  border-color: #303f9f;
}

.document-drop-zone--disabled {
  background: #f5f5f5;
  cursor: default;
  opacity: 0.75;
}

.document-preview {
  overflow: hidden;
}

.document-preview__pdf {
  border: 0;
  display: block;
  height: 360px;
  width: 100%;
}
</style>
